import React from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Alert,
  FormHelperText,
} from '@mui/material';
import { WA_GREEN } from '../whatsappPreview';
import { FlowComponent } from '../../../../flow-core/FlowComponent';
import { FooterCore } from '../../../../flow-core/components/FooterCore';

export const FooterComponent = new FlowComponent({
  ...FooterCore,
  renderPreview(properties, context = {}) {
    // Green once the screen can be submitted, grey while a required answer is missing, as
    // WhatsApp draws it. The phone preview says which (context.canSubmit); the canvas does not.
    const ready = context.canSubmit === true;
    return (
      <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider', opacity: properties.visible === false ? 0.5 : 1 }}>
        <Button
          variant="contained"
          fullWidth
          disableElevation
          sx={{
            borderRadius: 999, py: 1.25, textTransform: 'none', fontSize: 16, pointerEvents: 'none',
            ...(ready
              ? { bgcolor: WA_GREEN, color: '#fff' }
              : { bgcolor: 'grey.100', color: 'text.disabled', boxShadow: 'none' }),
          }}
        >
          {properties.label || 'Submit'}
        </Button>
      </Box>
    );
  },
  renderPropertyEditor(properties, onChange, context) {
    var action = properties['on-click-action'] || { name: 'complete', payload: {} };
    var isAutoManaged = context?.selectedComponent?.metadata?.autoManaged === true;

    // Navigation targets: every other screen in the flow. Users pick a screen by its title, they
    // never see or type the underlying screen id.
    var allScreens = context?.screens || [];
    var navTargets = allScreens.filter((s) => s.id !== context?.currentScreenId);
    var navTargetId = action.next?.name || '';
    var navTargetKnown = allScreens.some((s) => s.id === navTargetId);

    // With only one screen there is nowhere to navigate, so hide Navigate then.
    // Data Exchange posts to the flow endpoint (it does NOT navigate to another screen), so it
    // depends on the flow being dynamic, not on screen count, gating it on screens was a bug that
    // hid it for single-screen dynamic flows. In every case still show whichever action is currently
    // selected so a loaded flow's action isn't silently dropped and the Select never shows blank.
    var hasOtherScreens = navTargets.length > 0;
    var isDynamicFlow = !!context?.isDynamicFlow;
    var showNavigate = hasOtherScreens || action.name === 'navigate';
    var showDataExchange = isDynamicFlow || action.name === 'data_exchange';

    var handleActionChange = (field, value) => {
      // Changing the action TYPE must rebuild a clean action, each type carries different fields, so
      // spreading the old one would leave a stale `next` on a `complete`/`data_exchange` action (or a
      // stale `payload` on `navigate`), which is invalid Flow JSON.
      if (field === 'name') {
        var rebuilt;
        if (value === 'navigate') {
          rebuilt = { name: 'navigate', next: action.next || { type: 'screen', name: '' } };
        } else if (value === 'data_exchange') {
          rebuilt = { name: 'data_exchange', payload: action.payload || {} };
        } else {
          rebuilt = { name: 'complete', payload: action.payload || {} };
        }
        onChange('on-click-action', rebuilt);
        return;
      }
      onChange('on-click-action', {
        ...action,
        [field]: value,
      });
    };

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {isAutoManaged && (
          <Alert severity="info" sx={{ mb: 1 }}>
            Auto-managed Footer. Navigation updates automatically when screens are reordered.
          </Alert>
        )}
        <TextField
          fullWidth
          label="Button Label"
          value={properties.label || ''}
          onChange={(e) => onChange('label', e.target.value)}
          required
          size="small"
          helperText={`Text shown on the button (max 20 characters), ${(properties.label || '').length}/20`}
          error={(properties.label || '').length > 20}
        />

        <FormControl fullWidth size="small">
          <InputLabel id="footer-action-type-label">Action Type</InputLabel>
          <Select
            labelId="footer-action-type-label"
            value={action.name || 'complete'}
            onChange={(e) => handleActionChange('name', e.target.value)}
            label="Action Type"
          >
            <MenuItem value="complete">Complete</MenuItem>
            {showNavigate && <MenuItem value="navigate">Navigate</MenuItem>}
            {showDataExchange && <MenuItem value="data_exchange">Data Exchange</MenuItem>}
          </Select>
          {!hasOtherScreens && (
            <FormHelperText>Add another screen to enable navigation.</FormHelperText>
          )}
        </FormControl>

        {action.name === 'complete' && (
          <Box>
            <Typography variant="body2" gutterBottom>
              Payload (JSON)
            </Typography>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={3}
              value={JSON.stringify(action.payload || {}, null, 2)}
              onChange={(e) => {
                try {
                  const payload = JSON.parse(e.target.value);
                  handleActionChange('payload', payload);
                } catch (error) {
                  // Invalid JSON, ignore
                }
              }}
              placeholder='{"key": "value"}'
              helperText="Data to send when form is completed"
            />
          </Box>
        )}

        {action.name === 'navigate' && (
          <FormControl fullWidth size="small" error={!!navTargetId && !navTargetKnown}>
            {/* shrink + notched keep the floating label above the field so it doesn't overlap the
                "Select a screen…" placeholder shown while the value is empty (displayEmpty). */}
            <InputLabel id="footer-next-screen-label" shrink>Next Screen</InputLabel>
            <Select
              labelId="footer-next-screen-label"
              label="Next Screen"
              notched
              value={navTargetId}
              displayEmpty
              onChange={(e) =>
                handleActionChange('next', {
                  type: 'screen',
                  name: e.target.value,
                })
              }
            >
              <MenuItem value="">
                <em>Select a screen…</em>
              </MenuItem>
              {navTargets.map((screen) => (
                <MenuItem key={screen.id} value={screen.id}>
                  {screen.title || screen.id}
                </MenuItem>
              ))}
              {/* Keep a stale/unknown target visible so it isn't silently dropped on save. */}
              {navTargetId && !navTargetKnown && (
                <MenuItem value={navTargetId}>{navTargetId} (missing screen)</MenuItem>
              )}
            </Select>
            <FormHelperText>
              {navTargets.length === 0
                ? 'Add another screen to navigate to it.'
                : 'Screen to open when the button is tapped.'}
            </FormHelperText>
          </FormControl>
        )}

        {action.name === 'data_exchange' && (
          <Box>
            <Typography variant="body2" gutterBottom>
              Payload (JSON)
            </Typography>
            <TextField
              fullWidth
              size="small"
              multiline
              rows={3}
              value={JSON.stringify(action.payload || {}, null, 2)}
              onChange={(e) => {
                try {
                  const payload = JSON.parse(e.target.value);
                  handleActionChange('payload', payload);
                } catch (error) {
                  // Invalid JSON, ignore
                }
              }}
              placeholder='{"data": "${form.field_name}"}'
              helperText="Data to send to external endpoint"
            />
          </Box>
        )}
      </Box>
    );
  },
});
